import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Modal from "react-modal";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard, RoleGuard, StandardURL } from "../App";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faWallet,
  faPlus,
  faEdit,
  faTimes,
  faSpinner,
  faCreditCard,
  faBuilding,
  faUser,
  faKey,
  faBuildingColumns,
  faLock,
  faLockOpen,
  faIdCard,
  faToggleOn,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import { Oval } from "react-loader-spinner";


// Set app element for react-modal to avoid accessibility warnings
Modal.setAppElement("#root");

const MpesaSettings = () => {
  const navigate = useNavigate();

  const [mpesaAccount, setMpesaAccount] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Form states for adding/updating
  const [accountType, setAccountType] = useState("personal");
  const [mpesaCategory, setMpesaCategory] = useState("");
  const [consumerKey, setConsumerKey] = useState("");
  const [businessShortCode, setBusinessShortCode] = useState(""); // This is the state variable
  const [consumerSecret, setConsumerSecret] = useState("");
  const [passKey, setPassKey] = useState("");

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const propertyId = localStorage.getItem("selectedPropertyId");
  const token = localStorage.getItem("token");

  // Use a ref to track if the component is mounted
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true; // Set to true on mount

    const fetchSpecificMpesaAccount = async () => {
      if (!propertyId || !token) {
        setFetchError("Property not selected or user not authenticated.");
        setLoading(false);
        if (mounted.current) {
          toast.error("Property not selected or user not authenticated.");
          navigate("/");
        }
        return;
      }

      setLoading(true);
      setFetchError(null);
      try {
        const response = await axios.get(
          `${StandardURL}/iNyumba/admin/mpesaintroductioncontroller/view/${propertyId}`, // Endpoint might have changed to 'view'
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!mounted.current) return;

        const data = response.data.response; // This is the object you provided as 'expected response'
        setMpesaAccount(data);

        if (data) {
          setAccountType(data.mpesacategory || "personal");
          setMpesaCategory(data.mpesacategory || "");
          setConsumerKey(data.consumerkey || "");
          // Refactor: Use 'businessshortcode' from the backend response
          setBusinessShortCode(data.businessshortcode || "");
          setConsumerSecret(data.consumersecret || "");
          setPassKey(data.passkey || "");
          setIsEditMode(true);
          toast.success("M-Pesa account details loaded successfully!");
        } else {
          setIsEditMode(false);
          toast.info("No M-Pesa account configured for this property. Please add one.");
        }
      } catch (error) {
        if (!mounted.current) return;

        console.error("Error fetching M-Pesa account:", error);
        setFetchError(error.response?.data?.response || "Failed to fetch M-Pesa account details.");
        setMpesaAccount(null);
        setIsEditMode(false);
        toast.error(error.response?.data?.response || "Failed to fetch M-Pesa account details.");
      } finally {
        if (mounted.current) {
          setLoading(false);
        }
      }
    };

    fetchSpecificMpesaAccount();

    return () => {
      mounted.current = false;
    };
  }, [propertyId, token, navigate]);

  const handleAccountTypeChange = (e) => {
    const selectedType = e.target.value;
    setAccountType(selectedType);
    setMpesaCategory(selectedType); // Set M-Pesa Category to the selected account type (lowercase)
  };

  const openFormModal = (edit = false) => {
    setIsEditMode(edit);
    if (!edit) {
      setAccountType("personal");
      setMpesaCategory("personal"); // Default to 'personal' on add
      setConsumerKey("");
      setBusinessShortCode(""); // Ensure this is cleared for add mode
      setConsumerSecret("");
      setPassKey("");
    } else {
      if (mpesaAccount) {
        setAccountType(mpesaAccount.mpesacategory || "personal");
        setMpesaCategory(mpesaAccount.mpesacategory || "personal");
        setConsumerKey(mpesaAccount.consumerkey || "");
        // Refactor: Use 'businessshortcode' from the stored mpesaAccount
        setBusinessShortCode(mpesaAccount.businessshortcode || "");
        setConsumerSecret(mpesaAccount.consumersecret || "");
        setPassKey(mpesaAccount.passkey || "");
      }
    }
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    setShowFormModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    if (!propertyId || !token) {
      toast.error("Property not selected or user not authenticated.");
      setSubmitting(false);
      return;
    }

    const payload = {
      mpesacategory: mpesaCategory,
      // Note: Backend expectation for 'mpesaType' key. If it expects 'mpesacategory' for both type and category,
      // then 'mpesaType' might be redundant or map to the same. I'll keep it as 'mpesaType' for now,
      // assuming the backend differentiates, but if not, you might omit it or map it to mpesacategory.
      mpesaType: accountType,
    };

    if (accountType === "personal") {
      if (!consumerKey || !businessShortCode || !consumerSecret || !passKey) {
        toast.error("All Personal M-Pesa fields are required.");
        setSubmitting(false);
        return;
      }
      payload.consumerkey = consumerKey;
      // Refactor: Send 'businessshortcode' to the backend
      payload.bussinessshortcode = businessShortCode;
      payload.consumersecret = consumerSecret;
      payload.passkey = passKey;
    } else {
      // For common, explicitly clear personal fields if they might be sent by mistake
      payload.consumerkey = "";
      payload.bussinessshortcode = ""; // Refactor: Clear 'businessshortcode'
      payload.consumersecret = "";
      payload.passkey = "";
    }

    try {
      let response;
      if (isEditMode) {
        response = await axios.patch(
          `${StandardURL}/iNyumba/admin/mpesaintroductioncontroller/category/update/${propertyId}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(response.data.response || "M-Pesa account updated successfully!");
      } else {
        response = await axios.post(
          `${StandardURL}/iNyumba/admin/mpesaintroductioncontroller/add/${propertyId}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(response.data.response || "M-Pesa account added successfully!");
      }

      // After successful submission, the fetched mpesaAccount will already have the correct casing,
      // so simply setting it from the response data is good.
      setMpesaAccount(response.data.response);
      closeFormModal();
    } catch (error) {
      console.error("Error saving M-Pesa account:", error);
      toast.error(error.response?.data?.response || "Failed to save M-Pesa account.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <RoleGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8">
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-6 sm:p-8 lg:p-10">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-center mb-8 border-b pb-4">
              <button
                onClick={() => navigate("/landlord/home")}
                className="flex items-center text-gray-600 hover:text-gray-800 transition-colors duration-200 mb-4 sm:mb-0"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                Back to Dashboard
              </button>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-800 text-center flex-grow">
                <FontAwesomeIcon icon={faCreditCard} className="mr-3 text-indigo-600" />
                M-Pesa Settings
              </h1>
              {!loading && !mpesaAccount && (
                <button
                  onClick={() => openFormModal(false)}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 px-6 rounded-lg shadow-md transition-all duration-300 flex items-center justify-center"
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  Add M-Pesa Account
                </button>
              )}
            </div>

            {/* Loading State */}
            {loading && (
              <div className="flex flex-col items-center justify-center py-10">
                <Oval
                  height={50}
                  width={50}
                  color="#4F46E5"
                  wrapperStyle={{}}
                  wrapperClass=""
                  visible={true}
                  ariaLabel='loading-indicator'
                  secondaryColor="#C7D2FE"
                  strokeWidth={4}
                  strokeWidthSecondary={4}
                />
                <p className="ml-3 text-lg text-gray-700 mt-3">Loading M-Pesa settings...</p>
              </div>
            )}

            {/* Error State */}
            {fetchError && !loading && !mpesaAccount && (
              <div className="text-center py-10 bg-red-50 rounded-lg border border-red-300 text-red-700">
                <FontAwesomeIcon icon={faTimes} className="text-5xl text-red-400 mb-4" />
                <p className="text-lg font-semibold mb-2">Error Loading M-Pesa Settings</p>
                <p className="text-md">{fetchError}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-300"
                >
                  Retry
                </button>
              </div>
            )}

            {/* M-Pesa Account Details View */}
            {!loading && mpesaAccount && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8 shadow-sm">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold text-blue-800 flex items-center">
                    <FontAwesomeIcon icon={faCreditCard} className="mr-3 text-blue-600" />
                    Configured M-Pesa Account
                  </h2>
                  <button
                    onClick={() => openFormModal(true)}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-2.5 px-6 rounded-lg shadow-md transition-all duration-300 flex items-center justify-center"
                  >
                    <FontAwesomeIcon icon={faEdit} className="mr-2" />
                    Edit Account
                  </button>
                </div>
                <div className="space-y-3 text-gray-700">
  <p className="flex items-center">
    <FontAwesomeIcon icon={mpesaAccount.mpesacategory === 'personal' ? faUser : faBuilding} className="mr-2 text-blue-500" />
    <strong className="w-40">Account Type:</strong>{" "}
    <span className="capitalize">{mpesaAccount.mpesacategory || "N/A"}</span>
  </p>
  <p className="flex items-center">
    <FontAwesomeIcon icon={faCreditCard} className="mr-2 text-blue-500" />
    <strong className="w-40">M-Pesa Category:</strong> {mpesaAccount.mpesacategory || "N/A"}
  </p>
  {/* Now these fields will always display if mpesaAccount exists */}
  <p className="flex items-center">
    <FontAwesomeIcon icon={faKey} className="mr-2 text-blue-500" /> {/* Added icon */}
    <strong className="w-40">Consumer Key:</strong> {mpesaAccount.consumerkey || "N/A"}
  </p>
  <p className="flex items-center">
    <FontAwesomeIcon icon={faBuildingColumns} className="mr-2 text-blue-500" /> {/* Added icon */}
    <strong className="w-40">Business Short Code:</strong> {mpesaAccount.businessshortcode || "N/A"}
  </p>
  <p className="flex items-center">
    <FontAwesomeIcon icon={faLock} className="mr-2 text-blue-500" /> {/* Added icon */}
    <strong className="w-40">Consumer Secret:</strong> {mpesaAccount.consumersecret ? "********" : "N/A"}
  </p>
  <p className="flex items-center">
    <FontAwesomeIcon icon={faLockOpen} className="mr-2 text-blue-500" /> {/* Added icon */}
    <strong className="w-40">Pass Key:</strong> {mpesaAccount.passkey ? "********" : "N/A"}
  </p>
  {/* Add propertyId and status if you want to display them as well */}
  <p className="flex items-center">
    <FontAwesomeIcon icon={faIdCard} className="mr-2 text-blue-500" /> {/* Added icon */}
    <strong className="w-40">Property ID:</strong> {mpesaAccount.propertyId || "N/A"}
  </p>
  <p className="flex items-center">
    <FontAwesomeIcon icon={faToggleOn} className="mr-2 text-blue-500" /> {/* Added icon */}
    <strong className="w-40">Status:</strong>{" "}
    <span className="capitalize">{mpesaAccount.status || "N/A"}</span>
  </p>
</div>
</div>
            )}

            {/* No M-Pesa Account Configured */}
            {!loading && !mpesaAccount && !fetchError && (
              <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                <FontAwesomeIcon icon={faInfoCircle} className="text-5xl text-gray-400 mb-4" />
                <p className="text-lg text-gray-600">No M-Pesa account configured for this property.</p>
                <p className="text-md text-gray-500 mt-2">Click "Add M-Pesa Account" to set it up.</p>
              </div>
            )}
          </div>

          {/* Add/Update M-Pesa Account Modal */}
          <Modal
            isOpen={showFormModal}
            onRequestClose={closeFormModal}
            contentLabel={isEditMode ? "Update M-Pesa Account" : "Add M-Pesa Account"}
            className="modal-content fixed inset-0 flex items-center justify-center p-4"
            overlayClassName="modal-overlay fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center"
          >
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 relative">
              <button
                onClick={closeFormModal}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors duration-200"
                title="Close"
              >
                <FontAwesomeIcon icon={faTimes} size="lg" />
              </button>
              <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-3">
                {isEditMode ? "Update M-Pesa Account" : "Add M-Pesa Account"}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Account Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
                  <div className="flex gap-4">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name="accountType"
                        value="personal"
                        checked={accountType === "personal"}
                        onChange={handleAccountTypeChange}
                        className="form-radio text-indigo-600"
                      />
                      <span className="ml-2 text-gray-700">Personal</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name="accountType"
                        value="common"
                        checked={accountType === "common"}
                        onChange={handleAccountTypeChange}
                        className="form-radio text-indigo-600"
                      />
                      <span className="ml-2 text-gray-700">Common</span>
                    </label>
                  </div>
                </div>

                {/* M-Pesa Category */}
                <div>
                  <label htmlFor="mpesaCategory" className="block text-sm font-medium text-gray-700 mb-1">
                    M-Pesa Category
                  </label>
                  <input
                    type="text"
                    id="mpesaCategory"
                    value={mpesaCategory}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-gray-50 cursor-not-allowed"
                    required
                  />
                </div>

                {/* Conditional fields for Personal account type */}
                {accountType === "personal" && (
                  <>
                    <div>
                      <label htmlFor="consumerKey" className="block text-sm font-medium text-gray-700 mb-1">
                        Consumer Key
                      </label>
                      <input
                        type="text"
                        id="consumerKey"
                        value={consumerKey}
                        onChange={(e) => setConsumerKey(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="businessShortCode" className="block text-sm font-medium text-gray-700 mb-1">
                        Business Short Code
                      </label>
                      <input
                        type="text"
                        id="businessShortCode"
                        value={businessShortCode}
                        onChange={(e) => setBusinessShortCode(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="consumerSecret" className="block text-sm font-medium text-gray-700 mb-1">
                        Consumer Secret
                      </label>
                      <input
                        type="password"
                        id="consumerSecret"
                        value={consumerSecret}
                        onChange={(e) => setConsumerSecret(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="passKey" className="block text-sm font-medium text-gray-700 mb-1">
                        Pass Key
                      </label>
                      <input
                        type="password"
                        id="passKey"
                        value={passKey}
                        onChange={(e) => setPassKey(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        required
                      />
                    </div>
                  </>
                )}

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={closeFormModal}
                    className="px-5 py-2.5 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <Oval height={20} width={20} color="#FFF" secondaryColor="#C7D2FE" strokeWidth={5} strokeWidthSecondary={5} />
                    ) : (
                      <><FontAwesomeIcon icon={faPlus} className="mr-2" /> {isEditMode ? "Update Account" : "Add Account"}</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </Modal>
        </div>
      </AuthGuard>
    </RoleGuard>
  );
};

export default MpesaSettings;